import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search, Kanban, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/hooks/useAuth'
import { useAdminClientEngagements } from '@/features/admin/hooks/useAdminClientEngagements'
import { BoardScreen } from '@/features/projectBoards/components/BoardScreen'
import { CreateBoardDialog } from '@/features/projectBoards/components/CreateBoardDialog'
import type { ClientEngagementDocument, EngagementStatus } from '@/types'

type Engagement = ClientEngagementDocument & { id: string }

const STATUS_OPTIONS: Array<{ value: EngagementStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'awaiting_acceptance', label: 'Awaiting acceptance' },
  { value: 'awaiting_payment', label: 'Awaiting deposit' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
]

const STATUS_LABEL: Record<EngagementStatus, string> = {
  scheduled: 'Scheduled',
  awaiting_acceptance: 'Awaiting acceptance',
  awaiting_payment: 'Awaiting deposit',
  confirmed: 'Confirmed',
  in_progress: 'In progress',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
}

const STATUS_VARIANT: Record<
  EngagementStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  scheduled: 'secondary',
  awaiting_acceptance: 'outline',
  awaiting_payment: 'outline',
  confirmed: 'default',
  in_progress: 'default',
  delivered: 'outline',
  cancelled: 'destructive',
}

export function ProjectsBoardPage() {
  const { user } = useAuth()
  const currentUserId = user?.uid ?? ''
  const { engagements, loading, error, refresh } = useAdminClientEngagements()

  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get('engagementId')

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<EngagementStatus | 'all'>('all')
  const [showArchived, setShowArchived] = useState(false)
  const [createBoardFor, setCreateBoardFor] = useState<Engagement | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(true)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return engagements.filter((eng) => {
      if (!showArchived && eng.isArchived) return false
      if (statusFilter !== 'all' && eng.status !== statusFilter) return false
      if (q) {
        const hay = `${eng.clientName ?? ''} ${eng.title ?? ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [engagements, search, statusFilter, showArchived])

  // Selected engagement is always looked up by id from the FULL engagements list,
  // so filters/search hiding it from the sidebar do NOT clear the right pane.
  const selected = useMemo(
    () => engagements.find((e) => e.id === selectedId) ?? null,
    [engagements, selectedId]
  )

  function selectEngagement(id: string | null) {
    const next = new URLSearchParams(searchParams)
    if (id) next.set('engagementId', id)
    else next.delete('engagementId')
    setSearchParams(next, { replace: false })
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b px-6 py-4">
        <h1 className="text-xl font-semibold">Project Boards</h1>
        <p className="text-sm text-muted-foreground">
          All client engagement boards in one place.
        </p>
      </div>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Collapsed pull tab */}
        {!sidebarOpen && (
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="absolute left-0 top-4 z-10 inline-flex items-center gap-1 rounded-r-md border border-l-0 bg-card px-2 py-2 text-xs text-muted-foreground shadow-sm hover:bg-muted"
            aria-label="Expand engagement list"
            title="Expand engagement list"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </button>
        )}

        {/* Left sidebar */}
        {sidebarOpen && (
        <aside className="w-80 shrink-0 border-r flex flex-col">
          <div className="p-3 space-y-2 border-b">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Engagements
              </span>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Collapse engagement list"
                title="Collapse engagement list"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search engagements…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>

            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as EngagementStatus | 'all')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center justify-between pt-1">
              <label
                htmlFor="show-archived"
                className="text-sm text-muted-foreground cursor-pointer"
              >
                Show archived
              </label>
              <Switch
                id="show-archived"
                checked={showArchived}
                onCheckedChange={setShowArchived}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {loading && (
              <div className="p-3 text-sm text-muted-foreground">Loading engagements…</div>
            )}
            {error && <div className="p-3 text-sm text-destructive">{error}</div>}
            {!loading && !error && filtered.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground">
                No engagements match.
              </div>
            )}
            {filtered.map((eng) => {
              const isSelected = eng.id === selectedId
              return (
                <button
                  key={eng.id}
                  onClick={() => selectEngagement(eng.id)}
                  className={`w-full text-left rounded-md px-3 py-2 transition-colors space-y-1 border ${
                    isSelected
                      ? 'bg-primary/10 border-primary/40'
                      : 'border-transparent hover:bg-muted/60'
                  }`}
                >
                  <div className="font-semibold text-sm truncate">
                    {eng.clientName || 'Unnamed client'}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {eng.title || 'Untitled engagement'}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant={STATUS_VARIANT[eng.status]}
                      className="text-[10px]"
                    >
                      {STATUS_LABEL[eng.status]}
                    </Badge>
                    {!eng.boardId && (
                      <span className="text-[10px] text-muted-foreground">
                        (no board yet)
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </aside>
        )}

        {/* Right pane */}
        <main className="flex-1 min-w-0 flex flex-col">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 gap-3">
              <Kanban className="h-10 w-10 text-muted-foreground/50" />
              <p className="text-lg font-semibold">Pick an engagement on the left</p>
              <p className="text-sm text-muted-foreground max-w-xs">
                Select a client engagement to see (or create) its project board.
              </p>
            </div>
          ) : selected.boardId ? (
            <BoardScreen
              key={selected.boardId}
              rootBoardId={selected.boardId}
              currentUserId={currentUserId}
              engagementLineItems={selected.lineItems}
              showBackLink={false}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 gap-3">
              <Kanban className="h-10 w-10 text-muted-foreground/50" />
              <p className="text-lg font-semibold">
                This engagement doesn't have a project board yet.
              </p>
              <p className="text-sm text-muted-foreground max-w-md">
                Create one to start tracking tasks for{' '}
                <span className="font-medium">{selected.title || 'this engagement'}</span>.
              </p>
              <Button onClick={() => setCreateBoardFor(selected)}>Create board</Button>
            </div>
          )}
        </main>
      </div>

      {createBoardFor && (
        <CreateBoardDialog
          open={!!createBoardFor}
          engagement={createBoardFor}
          onClose={() => {
            setCreateBoardFor(null)
            refresh()
          }}
        />
      )}
    </div>
  )
}

export default ProjectsBoardPage
